import React from 'react'
import { UploadFile } from 'antd/es/upload'
import { Card, Form, Input, Tooltip, Tabs, Upload, Button, Modal, Spin, Tag, message } from 'antd'
import { LinkOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { UploadIcon, Trash } from 'lucide-react'

import { CardProps, PosterDisplayState } from '../../../types/card'
import { InstagramReel } from '../../../../types/instagram-reel'
import { MediaData } from '../../../../types/media-data'
import { VideoMetadata } from '../../../../types/videos'
import { useCardContext } from '../../context/card'

import { useExternalMediaMetadata } from '../../hooks/media/use-external-media'
import { useUploadMedia } from '../../hooks/carousel/use-upload-media'

import { SecondaryButton } from '../ui/secondary-button'
import { ReuseVideosModal } from './reuse-video-modal'
import { Poster } from './poster'
import validateUrl from '../../utils/url-validator'
import { tokens } from '../../theme/tokens'
import { typography } from '../../theme/typography'
import { validateFileSize, validateImageDimensions } from '../../utils/image-validator'
import {
  getVideoCdnUrl,
  isExternalPlatformUrl,
  isSupportedPlatformUrl,
  removeOptimizedPath,
} from '../../utils/media-utils'

import { getTooltipText, setStatus } from '../../utils/video-status-utils'

const MEDIA_DOMAIN = process.env.REACT_APP_MEDIA_DOMAIN

const FIXED_WIDTH = '400px'

export const CardContent: React.FC<CardProps> = ({ content, required, onFieldChange, isSaved, onDelete, readonly }) => {
  const { state, dispatch } = useCardContext()
  const { uploadVideo } = useUploadMedia()
  const { fetchExternalMetadata } = useExternalMediaMetadata()

  const isLoaded = !!content.url

  const [reuseVideoModal, setReuseVideoModal] = React.useState(false)
  const [deleteModal, setDeleteModal] = React.useState(false)
  const [externalMetadata, setExternalMetadata] = React.useState<MediaData>({
    mediaUrl: '',
    caption: '',
    thumbnail: '',
  })
  const [uploadingVideo, setUploadingVideo] = React.useState(false)
  const [uploadedVideoFileName, setUploadedVideoFileName] = React.useState('')
  const [uploadedPosterFileName, setUploadedPosterFileName] = React.useState('')
  const [posterPreviewUrl, setPosterPreviewUrl] = React.useState<string | undefined>(undefined)

  const [activeMode, setActiveMode] = React.useState(content.mode)
  const [isHovered, setIsHovered] = React.useState(false)

  const urlRef = React.useRef(content.url)

  const handleCancel = () => setDeleteModal(false)

  const handleDeleteClick = () => setDeleteModal(true)

  const handleConfirmDelete = () => {
    setDeleteModal(false)
    if (onDelete) {
      onDelete(content.id)
    }
  }

  // limpia el campo CTA si coincide con la URL cuando se enfoca
  const handleFocusCTA = React.useCallback(() => {
    if (content.cta === content.url && !readonly) {
      onFieldChange(content.id, 'cta', '')
    }
  }, [content.cta, content.url, onFieldChange, content.id, readonly])

  // setea el campo CTA a la URL si está vacío cuando se desenfoca
  const handleBlurCTA = React.useCallback(() => {
    if (content.cta.trim() === '' && content.mode === 'url') {
      onFieldChange(content.id, 'cta', content.url)
    }
  }, [content.cta, content.url, onFieldChange, content.id, content.mode])

  // setea el campo título el caption del video si está vacío cuando se desenfoca
  const handleBlurTitle = React.useCallback(() => {
    if (content.title.trim() === '' && externalMetadata.caption) {
      onFieldChange(content.id, 'title', externalMetadata.caption)
    }
  }, [content.title, externalMetadata.caption, onFieldChange, content.id])

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const url = e.target.value
    onFieldChange(id, 'url', url)

    dispatch({
      type: 'UPDATE_CARD',
      payload: {
        id,
        updates: { url },
      },
    })
  }

  // selecciona un video existente del historial
  const handleExistingVideoSelect = (selectedVideo: VideoMetadata | InstagramReel) => {
    const videoUrl =
      'displayUrl' in selectedVideo
        ? selectedVideo.url || ''
        : selectedVideo.originalUri || selectedVideo.videoUrl || ''

    const isDuplicate = state.cards.some(c => c.id !== content.id && c.url === videoUrl)

    if (isDuplicate) {
      message.error('Esta URL ya fue utilizada en otra tarjeta.')
      return
    }

    if ('displayUrl' in selectedVideo) {
      onFieldChange(content.id, 'url', selectedVideo.url || '')
      onFieldChange(content.id, 'cta', selectedVideo.url || '')
      onFieldChange(content.id, 'title', selectedVideo.caption || '')
      onFieldChange(content.id, 'posterUrl', selectedVideo.displayUrl || '')
    } else {
      const posterUrl = removeOptimizedPath(selectedVideo.posterUrl || '')
      onFieldChange(content.id, 'url', videoUrl)
      onFieldChange(content.id, 'cta', selectedVideo.cta || '')
      onFieldChange(content.id, 'title', selectedVideo.caption || '')
      onFieldChange(content.id, 'posterUrl', posterUrl)

      const isMediaUrl = videoUrl.includes(MEDIA_DOMAIN as string)
      const newMode = isMediaUrl ? 'upload' : 'url'
      dispatch({
        type: 'UPDATE_CARD',
        payload: {
          id: content.id,
          updates: {
            mode: newMode,
            status: selectedVideo.status,
          },
        },
      })
      setActiveMode(newMode)
    }

    setReuseVideoModal(false)
  }

  // cambia el modo de contenido entre URL, subir archivo
  const handleModeChange = (mode: string) => {
    if (mode === 'history') {
      setReuseVideoModal(true)
      return
    }

    if (mode !== activeMode) {
      setActiveMode(mode as 'url' | 'upload')

      dispatch({
        type: 'UPDATE_CARD',
        payload: {
          id: content.id,
          updates: { mode: mode as 'url' | 'upload' },
        },
      })
    }
  }

  const validateFile = async (file: UploadFile): Promise<boolean> => {
    const fileSizeValidation = validateFileSize(file as unknown as File)
    if (!fileSizeValidation.valid) {
      message.error(fileSizeValidation.message)
      return false
    }

    const dimensionsValidation = await validateImageDimensions(file as unknown as File)
    if (!dimensionsValidation.valid) {
      message.error(dimensionsValidation.message)
      return false
    }

    return true
  }

  const handlePosterUpload = (fileType: 'image') => {
    return {
      name: fileType,
      multiple: false,
      accept: 'image/*',
      showUploadList: false,
      beforeUpload: async (file: UploadFile) => {
        try {
          const isValid = await validateFile(file)
          if (!isValid) return false

          const fileToUpload = file as unknown as File
          const fileName = fileToUpload.name
          setUploadedPosterFileName(fileName)

          const previewUrl = URL.createObjectURL(fileToUpload)
          setPosterPreviewUrl(previewUrl)

          dispatch({
            type: 'SET_PENDING_POSTER',
            payload: {
              id: content.id,
              file: fileToUpload,
              fileName: fileName,
            },
          })

          onFieldChange(content.id, 'posterUrl', previewUrl)
        } catch (error) {
          console.error('CardContent | Error al subir archivo:', error)
        }
        return false
      },
    }
  }

  const handleVideoUpload = (fileType: 'video') => {
    return {
      name: fileType,
      multiple: false,
      accept: 'video/*',
      showUploadList: false,
      beforeUpload: async (file: UploadFile) => {
        try {
          setUploadingVideo(true)
          setUploadedVideoFileName('')
          const fileToUpload = file as unknown as File

          const extension = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase()}` : ''

          const res = await uploadVideo(fileType, extension, fileToUpload)

          if (res) {
            const domain = getVideoCdnUrl(res.fileKey)
            setUploadedVideoFileName(fileToUpload.name)
            onFieldChange(content.id, 'url', domain)

            dispatch({
              type: 'UPDATE_CARD',
              payload: {
                id: content.id,
                updates: { mode: 'upload', status: 'PROCESSING', videoId: res.videoId },
              },
            })
          }
        } catch (error) {
          console.error('CardContent | Error al subir archivo:', error)
        } finally {
          setUploadingVideo(false)
        }
        return false
      },
    }
  }

  const getPosterInfo = (): { posterState: PosterDisplayState; imageSource: string | undefined } => {
    // Priorizar la vista previa del póster si existe
    if (posterPreviewUrl) {
      return { posterState: 'preview', imageSource: posterPreviewUrl }
    }

    // Priorizar el póster existente
    if (content.posterUrl) {
      return { posterState: 'preview', imageSource: content.posterUrl }
    }

    // Manejo específico para modo url: mostramos el thumbnail
    if (activeMode === 'url') {
      if (content.url && externalMetadata.thumbnail) {
        return { posterState: 'preview', imageSource: externalMetadata.thumbnail }
      }
      return { posterState: 'empty', imageSource: undefined }
    }

    // Mostrar processing si está en ese estado
    if (content.status === 'PROCESSING') {
      return { posterState: 'processing', imageSource: undefined }
    }

    // Manejo específico para modo upload
    if (activeMode === 'upload' && content.url) {
      return { posterState: 'processing', imageSource: undefined }
    }

    // Si no se cumple ninguna condición, el estado por defecto es 'empty'
    return { posterState: 'empty', imageSource: undefined }
  }

  // Valida que la URL sea de Instagram o TikTok
  const validatePlatformUrl = () => {
    return [
      {
        required: true,
        message: 'Ingresa la URL de un Reel de Instagram o video de TikTok',
      },
      {
        validator: (_: any, value: string) => {
          if (!value) return Promise.resolve()
          if (!isSupportedPlatformUrl(value)) {
            return Promise.reject(new Error('Debe ser una URL de un Reel de Instagram o video de TikTok'))
          }
          return Promise.resolve()
        },
      },
    ]
  }

  const validateCTAUrl = () => {
    return [
      {
        required: true,
        message: 'Ingresa un CTA',
      },
      {
        validator: (_: any, value: string) => {
          if (!value) return Promise.resolve()
          if (!validateUrl(value)) {
            return Promise.reject(new Error('Debe ser una URL válida'))
          }
          return Promise.resolve()
        },
      },
    ]
  }

  React.useEffect(() => {
    setActiveMode(content.mode)
  }, [content.mode])

  // limpia el campo de vista previa si la URL es inválida
  React.useEffect(() => {
    if (!isSupportedPlatformUrl(content.url)) {
      setExternalMetadata(prev => ({ ...prev, thumbnail: '' }))
    }
  }, [content.url])

  React.useEffect(() => {
    if (!content.url) return
    const urlChanged = content.url !== urlRef.current

    const isSupportedUrl = isExternalPlatformUrl(content.url)

    const shouldFetch = urlChanged && !state.successfulCards.includes(content.id) && content.url && isSupportedUrl

    urlRef.current = content.url

    if (shouldFetch) {
      const fetchData = async () => {
        const fetchedMedia = content.url ? await fetchExternalMetadata([content.url]) : []
        const data = fetchedMedia[0] || {}

        if (data) {
          setExternalMetadata(data)
          if (data.caption) {
            onFieldChange(content.id, 'title', data.caption)
          }
        }
      }

      fetchData()
    }
  }, [content.id, content.url, state.successfulCards, onFieldChange, fetchExternalMetadata])

  React.useEffect(() => {
    return () => {
      if (posterPreviewUrl) {
        URL.revokeObjectURL(posterPreviewUrl)
      }
    }
  }, [posterPreviewUrl])

  const { posterState, imageSource } = getPosterInfo()

  return (
    <>
      <Card
        key={content.id}
        style={{
          marginBottom: '15px',
          height: 'auto',
          borderTopLeftRadius: '0',
          borderBottomLeftRadius: '0',
          position: 'relative',
          opacity: readonly ? 0.8 : 1,
        }}
        styles={{
          body: {
            paddingLeft: '20px',
            paddingBottom: '20px',
            paddingTop: '10px',
          },
        }}
      >
        {/* Status */}
        <>
          {isSaved && (
            <Tooltip
              title={getTooltipText(content.status, content.errorDetails)}
              placement="rightBottom"
              mouseEnterDelay={0.5}
            >
              <Tag
                style={{
                  height: '20px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'absolute',
                  top: '7%',
                  right: '50px',
                  gap: '8px',
                  backgroundColor: tokens.colors.primary.white,
                  fontSize: typography.label.fontSize,
                  padding: '0 10px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                {setStatus(content.status).text}
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: setStatus(content.status).color,
                  }}
                />
              </Tag>
            </Tooltip>
          )}
        </>
        {/* Botón de eliminar en la esquina superior derecha */}

        <>
          <Tooltip title={readonly ? '' : 'Eliminar Tarjeta'} placement="rightBottom" mouseEnterDelay={0.5}>
            <button
              type="button"
              onClick={handleDeleteClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                cursor: readonly ? 'not-allowed' : 'pointer',
                position: 'absolute',
                top: '5%',
                right: '5px',
                color: isHovered ? 'red' : 'gray',
              }}
              disabled={readonly}
            >
              <Trash width={14} />
            </button>
          </Tooltip>
          <Modal
            title="Confirmar Eliminación"
            open={deleteModal}
            onCancel={handleCancel}
            footer={[
              <Button key="back" onClick={handleCancel}>
                Cancelar
              </Button>,
              <SecondaryButton key="cancel" onClick={handleConfirmDelete}>
                Eliminar
              </SecondaryButton>,
            ]}
          >
            <p>¿Deseas eliminar esta tarjeta?</p>
          </Modal>
        </>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            paddingTop: '0px',
            alignItems: 'stretch',
          }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '18px', width: FIXED_WIDTH }}>
            <Tabs
              activeKey={activeMode}
              onChange={readonly ? undefined : handleModeChange}
              style={{ gap: '12px' }}
              items={[
                {
                  key: 'url',
                  label: 'URL',
                  disabled: readonly,
                  children: (
                    <Form.Item name={`link${content.id}`} rules={validatePlatformUrl()} style={{ margin: 0 }}>
                      <Input
                        value={content.url}
                        onChange={e => handleUrlChange(e, content.id)}
                        prefix={<LinkOutlined />}
                        placeholder="Pega tu URL aquí"
                        style={{ color: isLoaded ? '#A7A7A7' : 'inherit' }}
                        disabled={activeMode === 'upload'}
                        readOnly={readonly}
                      />
                    </Form.Item>
                  ),
                },
                {
                  key: 'upload',
                  label: 'Subir video',
                  disabled: readonly,
                  children: (
                    <div style={{ width: '100%' }}>
                      <Upload {...(readonly ? {} : handleVideoUpload('video'))}>
                        <Tooltip
                          title={
                            readonly ? (
                              ''
                            ) : (
                              <>
                                📐 Proporción 9:16 (vertical) <br />
                                🖼️ Mínimo 540x960 px <br />
                                🎞️ Formatos: mp4, mov, mpeg, m4v <br />
                                📦 Máx. 500MB
                              </>
                            )
                          }
                          placement="right"
                          mouseEnterDelay={0.5}
                        >
                          <Button
                            icon={
                              uploadingVideo ? (
                                <Spin size="small" />
                              ) : uploadedVideoFileName ? (
                                <CheckCircleOutlined style={{ color: '#white' }} />
                              ) : (
                                <UploadIcon width={12} />
                              )
                            }
                            disabled={uploadingVideo || readonly}
                            style={{ width: FIXED_WIDTH }}
                          >
                            {uploadingVideo
                              ? 'Subiendo...'
                              : uploadedVideoFileName
                                ? uploadedVideoFileName.length > 30
                                  ? `${uploadedVideoFileName.slice(0, 30)}...`
                                  : uploadedVideoFileName
                                : 'Subir video'}
                          </Button>
                        </Tooltip>
                      </Upload>
                    </div>
                  ),
                },
                {
                  key: 'history',
                  label: 'Historial',
                  disabled: readonly,
                },
              ]}
            />

            <Form.Item name={`cta${content.id}`} rules={validateCTAUrl()} style={{ margin: 0 }}>
              <Input
                value={content.cta}
                onChange={e => onFieldChange(content.id, 'cta', e.target.value)}
                onFocus={handleFocusCTA}
                onBlur={handleBlurCTA}
                placeholder="Ingresa el enlace del CTA"
                style={{ color: isLoaded ? '#A7A7A7' : 'inherit' }}
                readOnly={readonly}
              />
            </Form.Item>

            <Form.Item
              name={`title${content.id}`}
              rules={[
                {
                  required: required,
                  message: 'Ingresa un título',
                },
              ]}
              style={{ margin: 0 }}
            >
              <Input
                value={content.title}
                onChange={e => onFieldChange(content.id, 'title', e.target.value)}
                onBlur={handleBlurTitle}
                placeholder="Título del video"
                style={{ color: isLoaded ? '#A7A7A7' : 'inherit' }}
                readOnly={readonly}
              />
            </Form.Item>
          </div>

          <div
            style={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
              marginTop: '50px',
              minWidth: '100px',
            }}
          >
            <Poster image={imageSource} state={posterState} />

            <Upload {...handlePosterUpload('image')}>
              <Tooltip
                title={
                  readonly ? (
                    ''
                  ) : (
                    <>
                      📐 Proporción 9:16 (vertical) <br />
                      🖼️ Mínimo 540x960 px <br />
                      📦 Máx. 15MB
                    </>
                  )
                }
                placement="right"
                mouseEnterDelay={0.5}
              >
                <Button
                  icon={
                    uploadedPosterFileName ? (
                      <CheckCircleOutlined style={{ color: '#white' }} />
                    ) : (
                      <UploadIcon width={12} />
                    )
                  }
                  style={{ width: '130px' }}
                  disabled={readonly}
                >
                  {uploadedPosterFileName
                    ? uploadedPosterFileName.length > 12
                      ? `${uploadedPosterFileName.slice(0, 12)}...`
                      : uploadedPosterFileName
                    : 'Cambiar poster'}
                </Button>
              </Tooltip>
            </Upload>
          </div>

          {isSaved && (
            <Tooltip title="Contenido guardado" placement="right" mouseEnterDelay={0.5}>
              <CheckCircleOutlined
                style={{
                  color: '#119400',
                  marginLeft: '8px',
                  fontSize: '16px',
                  position: 'absolute',
                  right: '-24px',
                }}
              />
            </Tooltip>
          )}
        </div>
      </Card>

      <ReuseVideosModal
        visible={reuseVideoModal}
        onClose={() => setReuseVideoModal(false)}
        onSelect={handleExistingVideoSelect}
      />
    </>
  )
}

